import { useState, useCallback } from 'react';
import { chatConfig } from '../../config/chatConfig';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { TextFileRecord, ImageFileRecord } from '../../types/files';
import { ConvexMessage, MessageDocument } from '../../types/message';
import { checkIfFileIsAccepted, checkIfFileIsDuplicate, countTotalAttachments, getFileCategory } from './utils';
import { processImageFile, processTextFile } from './processors';

interface Props {
  messages: ConvexMessage[];
  setSalesModalFeature: (feature: string) => void;
  setShowSalesModal: (show: boolean) => void;
  isSpace?: boolean;
  spaceAttachments?: MessageDocument[];
}

/**
 * useFileHandler hook to encapsulate file state and related helper methods.
 */
export default function useFileHandler({
  messages,
  setSalesModalFeature,
  setShowSalesModal,
  isSpace = false,
  spaceAttachments = [],
}: Props) {
  const [textFiles, setTextFiles] = useState<TextFileRecord[]>([]);
  const [imageFiles, setImageFiles] = useState<ImageFileRecord[]>([]);
  const toast = useToast();
  const auth = useAuth();


  const internalRemoveTextFile = useCallback((id: string) => {
    setTextFiles((prev) => prev.filter((file) => file.id !== id));
  }, []);

  const handleRemoveTextFile = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>, id: string) => {
      e.preventDefault();
      internalRemoveTextFile(id);
    },
    [internalRemoveTextFile]
  );

  const removeImageFile = useCallback((id: string) => {
    setImageFiles((prev) => {
      const target = prev.find((f) => f.id === id);
      if (target?.previewUrl) {
        URL.revokeObjectURL(target.previewUrl);
      }
      return prev.filter((file) => file.id !== id);
    });
  }, []);

  const updateTextFileRecord = useCallback(
    (file: File, updates: Partial<TextFileRecord>) => {
      setTextFiles((prev) =>
        prev.map((f) =>
          f.name === file.name && f.size === file.size
            ? { ...f, ...updates, isLoaded: true }
            : f
        )
      );
    },
    []
  );

  const updateImageFileRecord = useCallback(
    (file: File, updates: Partial<ImageFileRecord>) => {
      setImageFiles((prev) =>
        prev.map((f) =>
          f.name === file.name && f.size === file.size
            ? { ...f, ...updates, isLoaded: true }
            : f
        )
      );
    },
    []
  );

  const handleFileError = useCallback(
    (file: TextFileRecord | ImageFileRecord, error: any, fallbackMsg?: string) => {
      internalRemoveTextFile(file.id);
      removeImageFile(file.id);
      const message = fallbackMsg || `Error processing ${file.name}`;
      toast(message, 'error');
      console.error(`Error processing file ${file.name}:`, error);
    },
    [internalRemoveTextFile, removeImageFile, toast]
  );

  const processFiles = useCallback(
    (eventOrOptions: any) => {
      const event = eventOrOptions.e || eventOrOptions;
      const files: FileList | null = event?.target?.files;

      if (!auth.user) {
        toast('Sign in to Upload Files', 'error');
        return;
      }

      if (!auth.checkUsageData()) {
        toast('No messages remaining. Please upgrade your plan to continue uploading files.', 'error');
        return;
      }

      if (!files || files.length === 0) {
        return;
      }

      const filesArray = Array.from(files);
      const currentPendingFiles =
        (textFiles?.length || 0) + (imageFiles?.length || 0);
      const remainingMessageSlots =
        isSpace ? chatConfig.limits.files.MAX_FILES_PER_SPACEUPLOAD : chatConfig.limits.files.MAX_FILES_PER_MESSAGE - currentPendingFiles;
      if (remainingMessageSlots <= 0) {
        toast(
          `You can only attach up to ${isSpace ? chatConfig.limits.files.MAX_FILES_PER_SPACEUPLOAD : chatConfig.limits.files.MAX_FILES_PER_MESSAGE} files per message.`,
          'error'
        );
        return;
      }

      const totalExistingAttachments =
        countTotalAttachments(messages, isSpace, spaceAttachments) + currentPendingFiles;

      let maxFiles;
      if (isSpace) {
        maxFiles = chatConfig.limits.files.MAX_FILES_PER_SPACE;
      } else {
        const tier = auth.subscription?.plan?.toLowerCase();

        // Define the valid tiers as in your config
        const validTiers = ['free', 'premium', 'ultimate'] as const;

        // Only use the tier if it is valid; otherwise, fallback to 'free'
        const userTier: typeof validTiers[number] =
          tier && validTiers.includes(tier as any) ? (tier as typeof validTiers[number]) : 'free';

        maxFiles = chatConfig.limits.files.MAX_FILES_PER_CHAT[userTier];
      }

      const remainingTierSlots = maxFiles - totalExistingAttachments;
      if (remainingTierSlots <= 0) {
        if (!isSpace && ['free', 'premium', 'ultimate'].includes(auth.subscription?.plan?.toLowerCase() || '')) {
          setSalesModalFeature('Additional File Uploads');
          setShowSalesModal(true);
        } else {
          toast(
            `You've reached the maximum of ${maxFiles} files for this ${isSpace ? 'space' : 'chat'}.`,
            'error'
          );
        }
        return;
      }

      const filesToProcess = filesArray.slice(
        0,
        Math.min(remainingMessageSlots, remainingTierSlots)
      );
      const seenInBatch = new Set<string>();

      (async () => {
        for (const file of filesToProcess) {
          const key = `${file.name}-${file.size}`;
          if (seenInBatch.has(key) || checkIfFileIsDuplicate(file, textFiles, imageFiles)) {
            toast(`${file.name} already attached`, 'error');
            continue;
          }
          seenInBatch.add(key);

          const fileSizeInMB = file.size / (1024 * 1024);
          if (!checkIfFileIsAccepted(file)) {
            if (fileSizeInMB > chatConfig.limits.files.MAX_FILE_SIZE_MB) {
              toast(
                `${file.name} exceeds the maximum size of ${chatConfig.limits.files.MAX_FILE_SIZE_MB}MB`,
                'error'
              );
            } else {
              toast(`File type ${file.type} not supported`, 'error');
            }
            continue;
          }

          const category = getFileCategory(file);
          if (!category) {
            toast(`File type ${file.type || 'unknown'} not supported`, 'error');
            continue;
          }
          if (category === 'image/png' || category === 'image/jpeg' || category === 'image/jpg') {
            try {
              const fileRecord = await processImageFile(
                file,
                updateImageFileRecord,
                handleFileError
              );
              if (fileRecord) {
                setImageFiles((prev) => [...prev, fileRecord]);
              } else {
                toast(`File type ${file.type} not supported`, 'error');
              }
            } catch {
              // error handled in handleFileError
            }
          } else {
            try {
              const fileRecord = await processTextFile(
                file,
                updateTextFileRecord,
                handleFileError
              );
              if (fileRecord) {
                setTextFiles((prev) => [...prev, fileRecord]);
              } else {
                toast(`File type ${file.type} not supported`, 'error');
              }
            } catch {
              // error handled in handleFileError
            }
          }
        }
      })();

      if (files.length > filesToProcess.length) {
        toast(
          `Only processing first ${filesToProcess.length} files due to limits`,
          'error'
        );
      }

      if (event.target) event.target.value = '';
    },
    [
      auth,
      textFiles,
      imageFiles,
      messages,
      toast,
      setSalesModalFeature,
      setShowSalesModal,
      updateTextFileRecord,
      updateImageFileRecord,
      handleFileError,
      spaceAttachments,
      isSpace,
    ]
  );

  // Handler to trigger file input click.
  const handleAttachClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, fileInputRef: React.RefObject<HTMLInputElement>) => {
      event.preventDefault();

      if (!auth.user) {
        toast('Sign in to Upload Files', 'error');
        return;
      }

      if (!auth.checkUsageData()) {
        toast('No messages remaining. Please upgrade your plan to continue uploading files.', 'error');
        return;
      }

      const currentPendingFiles =
        (textFiles?.length || 0) + (imageFiles?.length || 0);
      const maxFilesPerMessage = isSpace ? chatConfig.limits.files.MAX_FILES_PER_SPACEUPLOAD : chatConfig.limits.files.MAX_FILES_PER_MESSAGE;
      if (currentPendingFiles >= maxFilesPerMessage) {
        toast(
          `You can only attach up to ${maxFilesPerMessage} files per ${isSpace ? 'space upload' : 'message'}.`,
          'error'
        );
        return;
      }

      const totalExistingAttachments =
        countTotalAttachments(messages, isSpace, spaceAttachments) + currentPendingFiles;

      let maxFiles;
      if (isSpace) {
        // Use space-specific limit when in a space
        maxFiles = chatConfig.limits.files.MAX_FILES_PER_SPACE;
      } else {
        // For regular chats, use tier-based limits
        // Get the tier from the authenticated user
        const tier = auth.subscription?.plan?.toLowerCase();

        // Define the valid tiers as in your config
        const validTiers = ['free', 'premium', 'ultimate'] as const;

        // Only use the tier if it is valid; otherwise, fallback to 'free'
        const userTier: typeof validTiers[number] =
          tier && validTiers.includes(tier as any) ? (tier as typeof validTiers[number]) : 'free';

        maxFiles = chatConfig.limits.files.MAX_FILES_PER_CHAT[userTier];
      }

      if (totalExistingAttachments >= maxFiles) {
        if (!isSpace && ['free', 'premium', 'ultimate'].includes(auth.subscription?.plan?.toLowerCase() || '')) {
          setSalesModalFeature('Additional File Uploads');
          setShowSalesModal(true);
        } else {
          toast(
            `You've reached the maximum of ${maxFiles} files for this ${isSpace ? 'space' : 'chat'}.`,
            'error'
          );
        }
        return;
      }

      if (fileInputRef?.current) {
        fileInputRef.current.click();
      }
    },
    [
      auth,
      textFiles,
      imageFiles,
      messages,
      toast,
      setSalesModalFeature,
      setShowSalesModal,
      spaceAttachments,
      isSpace,
    ]
  );

  return {
    textFiles,
    imageFiles,
    setTextFiles,
    setImageFiles,
    processFiles,
    handleAttachClick,
    // Export the click-handler version so that it prevents default submission.
    removeTextFile: handleRemoveTextFile,
    removeImageFile,
  };
}